import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useNotification } from '@/hooks/useNotification';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { isAdminUser } from '@/utils/role';

export const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { showNotification, NotificationContainer } = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      showNotification(t('validation.fillAllFields'), 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await login({ email, password });
      showNotification(t('notifications.loginSuccess'), 'success');
      
      // Déterminer la redirection basée sur le rôle (roleName, pas un ID fixe)
      const redirectPath = isAdminUser(response?.user) ? '/admin/dashboard' : '/mes-commandes';
      
      // Attendre un court délai pour que la session soit complètement établie
      setTimeout(() => {
        navigate(redirectPath);
      }, 100);
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : t('auth.loginError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <NotificationContainer />
      <div className="login-container">
        {/* Left Section - Form */}
        <div className="login-form-section">
          <div className="login-content">
            {/* Bouton retour à l'accueil */}
            <Link to="/" className="auth-back-button">
              <ArrowLeftIcon className="auth-back-icon" />
              <span>{t('authPages.backHome')}</span>
            </Link>
            
            <h1 className="login-greeting">
              {t('authPages.loginTitlePrefix')}{' '}
              <span className="text-highlight">{t('authPages.loginTitleHighlight')}</span>
            </h1>
            <p className="login-instruction">
              {t('authPages.loginInstruction')}
            </p>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">{t('authPages.email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  placeholder={t('authPages.email')}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">{t('authPages.password')}</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="form-input"
                    placeholder={t('authPages.passwordPlaceholder')}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('authPages.hidePassword', { defaultValue: 'Masquer le mot de passe' }) : t('authPages.showPassword', { defaultValue: 'Afficher le mot de passe' })}
                    aria-pressed={showPassword}
                  >
                    <svg
                      className="eye-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="form-remember">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <span>{t('authPages.rememberMe')}</span>
                </label>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <span className="button-loader"></span>
                    {t('authPages.loginLoading')}
                  </>
                ) : (
                  t('authPages.loginButton')
                )}
              </button>
            </form>

            <div className="login-signup">
              <p>
                {t('authPages.noAccount')}{' '}
                <Link to="/register" className="signup-link">
                  {t('authPages.signupLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right Section - Branding */}
        <div className="login-branding-section">
          <div className="auth-hero-panel">
            <div className="auth-hero-overlay" />
            <div className="auth-hero-content">
              <p className="auth-hero-kicker">{t('authPages.heroKicker')}</p>
              <h2 className="gtitle auth-hero-title">
                <span className="gtitle-light">{t('authPages.heroWelcomeLight')}</span>
                <span className="gtitle-strong">{t('authPages.heroWelcomeStrong')}</span>
              </h2>
              <p className="auth-hero-subtitle">{t('authPages.heroSubtitle')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

